<template>
  <div>
  <v-dialog v-model="visible" persistent max-width="740" scrollable @click:outside="close">
    <v-card style="position: relative;">
      <v-overlay :value="submitting || batchesLoading" absolute opacity="0.12" z-index="3">
        <v-progress-circular indeterminate color="success" size="48"></v-progress-circular>
      </v-overlay>
      <v-toolbar color="success darken-2" dark flat>
        <v-toolbar-title class="font-weight-bold text-subtitle-1">
          {{ $t('memberDataSection.greenCoffee.editTitle') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark :disabled="submitting || batchesLoading || outturnChecking" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0" style="max-height: calc(90vh - 120px); overflow-y: auto;">
        <div v-if="loading" class="d-flex justify-center align-center pa-10">
          <v-progress-circular indeterminate color="success"></v-progress-circular>
        </div>

        <v-form v-else-if="detail" ref="form" lazy-validation>
          <!-- Summary Row -->
          <div class="px-6 pt-5 pb-4">
            <v-row>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.outturnNumber') }}</div>
                <v-text-field
                  v-model="form.outturnNumber"
                  dense
                  outlined
                  hide-details="auto"
                  :loading="outturnChecking"
                  :error-messages="outturnError"
                  :success-messages="outturnSuccess"
                  :disabled="submitting || batchesLoading"
                  class="mt-1"
                  @input="onOutturnInput"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalQuantity') }}</div>
                <v-text-field
                  v-model.number="form.totalGreenBeans"
                  type="number"
                  dense
                  outlined
                  hide-details="auto"
                  class="mt-1"
                  :rules="[v => (v !== null && v !== '' && Number(v) > 0) || $t('memberDataSection.greenCoffee.totalGreenBeansRequired')]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('status') }}</div>
                <v-btn x-small rounded min-width="160" depressed class="gray-button" height="34">
                  {{ $t('memberDataSection.pending') }}
                </v-btn>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.startDate') }}</div>
                <v-menu v-model="purchaseDateMenu" :close-on-content-click="false" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.purchaseDate"
                      dense
                      outlined
                      readonly
                      hide-details
                      class="mt-1"
                      append-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="form.purchaseDate" no-title scrollable @input="purchaseDateMenu = false"></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </div>

          <v-divider></v-divider>

          <!-- Parchment Information -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('parchment')">
            <span class="section-title">{{ $t('memberDataSection.greenCoffee.parchmentInformation') }}</span>
            <v-icon>{{ sections.parchment ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.parchment">
              <div class="px-6 py-3">
                <label class="field-label d-block mb-1">{{ $t('memberDataSection.greenCoffee.selectProcessingBatch') }}</label>
                <v-select
                  v-model="selectedProcessingBatchIds"
                  :items="processingBatchSelectItems"
                  item-text="display"
                  item-value="id"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  dense
                  outlined
                  hide-details
                  :disabled="submitting || batchesLoading"
                  :placeholder="$t('memberDataSection.pOrder.makeSelection')"
                  class="processing-batch-select"
                >
                  <template v-slot:item="{ item, on, attrs }">
                    <v-list-item v-bind="attrs" v-on="on">
                      <v-list-item-action class="ma-0 mr-2">
                        <v-checkbox
                          :input-value="attrs.inputValue"
                          color="primary"
                          hide-details
                          class="mt-0 pt-0"
                          readonly
                        ></v-checkbox>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>{{ item.display }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-select>
              </div>

              <template v-if="form.batches.length">
                <div v-for="(batch, idx) in form.batches" :key="'batch-' + (batch.batchCode || batch.batchId || idx)">
                  <div class="px-6 py-4">
                    <v-row>
                      <v-col cols="12" sm="4">
                        <div class="field-label">{{ $t('memberDataSection.greenCoffee.batchId') }}</div>
                        <div class="field-value">{{ batch.batchId || '—' }}</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="field-label">{{ $t('memberDataSection.greenCoffee.quantity') }}</div>
                        <v-text-field
                          v-model.number="batch.quantity"
                          type="number"
                          dense
                          outlined
                          hide-details
                          class="mt-1"
                          :disabled="submitting || batchesLoading"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="field-label">{{ $t('memberDataSection.greenCoffee.supplier') }}</div>
                        <div class="field-value">{{ batch.supplier || '—' }}</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="field-label">{{ $t('memberDataSection.greenCoffee.species') }}</div>
                        <div class="field-value">{{ batch.species || '—' }}</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="field-label">{{ $t('memberDataSection.greenCoffee.variety') }}</div>
                        <div class="field-value">{{ batch.variety || '—' }}</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="field-label">{{ $t('memberDataSection.greenCoffee.process') }}</div>
                        <div class="field-value">{{ batch.process || '—' }}</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="field-label">{{ $t('memberDataSection.greenCoffee.batchRating') }}</div>
                        <div class="field-value">{{ batch.batchRating || '—' }}</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="field-label">{{ $t('memberDataSection.greenCoffee.humidity') }}</div>
                        <div class="field-value">{{ batch.humidity != null ? batch.humidity + ' %' : '—' }}</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="field-label">{{ $t('memberDataSection.greenCoffee.density') }}</div>
                        <div class="field-value">{{ batch.density != null ? batch.density + ' kg/cm3' : '—' }}</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalCoffeeCherryQty') }}</div>
                        <div class="field-value">{{ batch.totalCoffeeCherryQty != null ? batch.totalCoffeeCherryQty + ' kg' : '—' }}</div>
                      </v-col>
                    </v-row>
                  </div>
                  <v-divider v-if="idx < form.batches.length - 1"></v-divider>
                </div>
              </template>
              <div v-else class="px-6 py-4 text--secondary text-body-2">{{ $t('memberDataSection.greenCoffee.noParchmentBatches') }}</div>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Process Information -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('process')">
            <span class="section-title">{{ $t('memberDataSection.greenCoffee.processInformation') }}</span>
            <v-icon>{{ sections.process ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.process" class="px-6 py-4">
              <v-row>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.airHumidity') }}</div>
                  <v-text-field v-model="form.airHumidity" type="number" dense outlined hide-details class="mt-1"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.density') }}</div>
                  <v-text-field v-model="form.density" type="number" dense outlined hide-details class="mt-1"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.batchProductionDensity') }}</div>
                  <v-text-field v-model="form.batchProductionDensity" type="number" dense outlined hide-details class="mt-1"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.qualityScore') }}</div>
                  <v-select
                    v-model="form.greenCoffeeQualityScoreId"
                    :items="qualityScores"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details
                    class="mt-1"
                    :placeholder="$t('memberDataSection.pOrder.makeSelection')"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.weightBeforeProcessing') }}</div>
                  <v-text-field v-model="form.weightBeforeProcessing" type="number" dense outlined hide-details class="mt-1"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentagePrimaryDefect') }}</div>
                  <v-text-field v-model="form.primaryDefect" type="number" dense outlined hide-details class="mt-1"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentageSecondaryDefect') }}</div>
                  <v-text-field v-model="form.secondaryDefect" type="number" dense outlined hide-details class="mt-1"></v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Green Beans Information -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('greenBeans')">
            <span class="section-title">{{ $t('memberDataSection.greenCoffee.greenBeansInformation') }}</span>
            <v-icon>{{ sections.greenBeans ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.greenBeans" class="px-6 py-4">
              <v-row>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.outturnNumber') }}</div>
                  <div class="field-value">{{ displayGreenBeansId }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.numberOfBags') }}</div>
                  <div class="field-value">{{ totalBags != null ? totalBags : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.leftoverQuantity') }}</div>
                  <div class="field-value">{{ leftoverQuantity }} kg</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.unitSize') }}</div>
                  <v-text-field v-model.number="form.unitSize" type="number" dense outlined hide-details class="mt-1"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.expiryDate') }}</div>
                  <v-menu v-model="expiryDateMenu" :close-on-content-click="false" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.expiryDate"
                        dense
                        outlined
                        readonly
                        hide-details
                        class="mt-1"
                        append-icon="mdi-calendar"
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="form.expiryDate" no-title scrollable @input="expiryDateMenu = false"></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Traceability Label -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('traceability')">
            <span class="section-title">{{ $t('memberDataSection.greenCoffee.traceabilityLabel') }}</span>
            <v-icon>{{ sections.traceability ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.traceability" class="px-6 py-4">
              <v-row>
                <v-col cols="12" sm="6">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.selectProductType') }}</div>
                  <v-select
                    v-model="form.productTypeId"
                    :items="productTypes"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details
                    class="mt-1"
                    :placeholder="$t('memberDataSection.pOrder.makeSelection')"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.label') }}</div>
                  <v-select
                    v-model="form.labelId"
                    :items="labels"
                    item-text="title"
                    item-value="id"
                    dense
                    outlined
                    hide-details
                    class="mt-1"
                    :placeholder="$t('memberDataSection.pOrder.makeSelection')"
                  ></v-select>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Quality Control -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('quality')">
            <span class="section-title">{{ $t('memberDataSection.greenCoffee.qualityControl') }}</span>
            <v-icon>{{ sections.quality ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.quality">
              <div v-if="pieSegments.length" class="d-flex align-center px-6 pt-4 pb-2">
                <svg width="220" height="220" viewBox="0 0 220 220" class="flex-shrink-0">
                  <path v-for="seg in pieSegments" :key="'pie-' + seg.lotId" :d="seg.path" :fill="seg.color" />
                </svg>
                <div class="ml-6">
                  <div v-for="seg in chartSegments" :key="'legend-' + seg.lotId" class="d-flex align-center mb-2">
                    <span class="legend-dot mr-2" :style="{ backgroundColor: seg.color }"></span>
                    <span class="text-body-2">{{ seg.lotId }}: {{ seg.percentage }}%</span>
                  </div>
                </div>
              </div>

              <div v-for="(lot, idx) in qualityGradingLots" :key="'lot-' + (lot.id || idx)">
                <v-divider></v-divider>
                <div class="px-6 py-4">
                  <div class="lot-id mb-3">{{ $t('memberDataSection.greenCoffee.lotId') }}: {{ lot.lotId || '—' }}</div>
                  <v-row>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.quantity') }}</div>
                      <div class="field-value">{{ lot.quantity != null ? lot.quantity + ' kg' : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.packing') }}</div>
                      <div class="field-value">{{ lot.unitSize != null ? lot.unitSize + ' kg Bag' : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalBags') }}</div>
                      <div class="field-value">{{ lot.totalBags != null ? lot.totalBags : (lot.pocketQuantity != null ? lot.pocketQuantity : '—') }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.leftoverQuantity') }}</div>
                      <div class="field-value">{{ formatLeftover(lot) }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.qualityScore') }}</div>
                      <div class="field-value">{{ lot.qualityScore || '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.label') }}</div>
                      <div class="field-value">{{ displayLotLabel(lot) }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentage') }}</div>
                      <div class="field-value">{{ lot.percentage != null ? lot.percentage + '%' : lotPercentage(lot) }}</div>
                    </v-col>
                  </v-row>
                </div>
              </div>
              <div v-if="!qualityGradingLots.length" class="px-6 py-4 text--secondary text-body-2">
                {{ $t('memberDataSection.greenCoffee.noQualityGrades') }}
              </div>
              <div class="px-6 pb-4">
                <v-btn outlined color="primary" class="text-none" :disabled="submitting || batchesLoading" @click="openQualityGradingModal">
                  <v-icon left small>mdi-ribbon</v-icon>
                  {{ $t('memberDataSection.greenCoffee.qualityControlButton') }}
                </v-btn>
              </div>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Waste -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('waste')">
            <span class="section-title">{{ $t('memberDataSection.greenCoffee.waste') }}</span>
            <v-icon>{{ sections.waste ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.waste" class="px-6 py-4">
              <v-row>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalWaste') }}</div>
                  <v-text-field v-model.number="form.totalWaste" type="number" dense outlined hide-details class="mt-1"></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentage') }}</div>
                  <div class="field-value mt-3">{{ wastePercentage }}</div>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </v-form>

        <div v-else-if="!loading" class="pa-10 text-center text--secondary">{{ $t('memberDataSection.greenCoffee.noDataFound') }}</div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" class="text-none mr-2" :disabled="submitting || batchesLoading || outturnChecking" @click="close">{{ $t('cancel') }}</v-btn>
        <v-btn color="brown darken-2" class="white--text text-uppercase" depressed :loading="submitting" :disabled="batchesLoading || outturnChecking" @click="submit">
          {{ $t('submit') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <GreenBeansQualityGradingModal
    v-model="qualityGradingModalVisible"
    :outturn-number="form.outturnNumber || displayOutturn"
    :total-quantity="form.totalGreenBeans"
    :unit-size="form.unitSize"
    :lots="qualityGradingLots"
    :quality-scores="qualityScores"
    :labels="labels"
    :grade-options="gradeOptions"
    :processing-batch-code="processingBatchCode"
    @finish-grading="onQualityGradingFinished"
  />
  </div>
</template>

<script>
import moment from "moment";
import GreenCoffeeService from "@/_services/GreenCoffeeService";
import GreenBeansQualityGradingModal from "./GreenBeansQualityGradingModal.vue";
import { isKenyaClient } from "@/utils";

const PIE_COLORS = ["#4caf50", "#ff9800", "#607d8b", "#2196f3", "#9c27b0", "#f44336", "#00bcd4"];

export default {
  name: "EditGreenCoffeeModal",
  components: { GreenBeansQualityGradingModal },
  props: {
    value: { type: Boolean, default: false },
    itemId: { type: [Number, String], default: null },
  },
  data() {
    return {
      visible: this.value,
      loading: false,
      submitting: false,
      batchesLoading: false,
      outturnChecking: false,
      outturnError: "",
      outturnSuccess: "",
      outturnDebounceTimer: null,
      detail: null,
      purchaseDateMenu: false,
      expiryDateMenu: false,
      qualityGradingModalVisible: false,
      qualityGradingLots: [],
      processingBatches: [],
      syncingBatchSelection: false,
      originalOutturnNumber: "",
      productTypes: [],
      qualityScores: [],
      labels: [],
      gradeOptions: [],
      form: {
        outturnNumber: "",
        purchaseDate: "",
        totalGreenBeans: null,
        airHumidity: "",
        density: "",
        batchProductionDensity: "",
        greenCoffeeQualityScoreId: null,
        weightBeforeProcessing: "",
        primaryDefect: "",
        secondaryDefect: "",
        unitSize: isKenyaClient() ? 60 : null,
        expiryDate: "",
        productTypeId: null,
        labelId: null,
        totalWaste: null,
        batches: [],
      },
      sections: {
        parchment: true,
        process: true,
        greenBeans: true,
        traceability: true,
        quality: true,
        waste: true,
      },
    };
  },
  computed: {
    displayOutturn() {
      const v = this.detail?.outturnNumber;
      return v && v !== "—" ? v : "—";
    },
    displayGreenBeansId() {
      const v = this.detail?.greenBeansId;
      return v && v !== "—" ? v : "—";
    },
    processingBatchCode() {
      const firstBatch = (this.form.batches || [])[0] || (this.detail?.batches || [])[0];
      const batchId = firstBatch?.batchId || firstBatch?.batchCode;
      if (batchId && batchId !== "—") return batchId;
      const outturn = this.form.outturnNumber || (this.displayOutturn !== "—" ? this.displayOutturn : "");
      return outturn || "";
    },
    processingBatchSelectItems() {
      return this.processingBatches || [];
    },
    selectedProcessingBatchIds: {
      get() {
        return (this.form.batches || [])
          .map((b) => this.resolveProcessingBatchId(b))
          .filter((id) => id != null);
      },
      set(ids) {
        if (this.syncingBatchSelection) return;
        this.syncingBatchSelection = true;
        try {
          this.syncBatchesFromSelectedIds(ids || []);
        } finally {
          this.$nextTick(() => {
            this.syncingBatchSelection = false;
          });
        }
      },
    },
    totalBags() {
      const total = Number(this.form.totalGreenBeans) || 0;
      const unit = Number(this.form.unitSize) || 1;
      return unit ? Math.floor(total / unit) : 0;
    },
    leftoverQuantity() {
      const total = Number(this.form.totalGreenBeans) || 0;
      const unit = Number(this.form.unitSize) || 1;
      return unit ? total % unit : total;
    },
    calculatedTotalWaste() {
      const total = Number(this.form.totalGreenBeans) || 0;
      if (!total) return null;
      const lotsSum = (this.qualityGradingLots || []).reduce(
        (sum, lot) => sum + (Number(lot.quantity) || 0),
        0
      );
      return Math.max(0, total - lotsSum);
    },
    effectiveTotalWaste() {
      if (this.form.totalWaste != null && this.form.totalWaste !== "") {
        return Number(this.form.totalWaste) || 0;
      }
      return this.calculatedTotalWaste;
    },
    wastePercentage() {
      const waste = this.effectiveTotalWaste;
      if (waste == null) return "—";
      const total = Number(this.form.totalGreenBeans) || 0;
      if (!total) return "—";
      return Math.round((waste / total) * 100) + " %";
    },
    chartSegments() {
      const totalGreenBeans = Number(this.form.totalGreenBeans) || 0;
      const totalWaste = this.effectiveTotalWaste;
      const hasWasteValue = totalWaste != null && Number(totalWaste) > 0;
      if (!totalGreenBeans) {
        return hasWasteValue ? [{ lotId: "Waste", percentage: 100, color: "#bdbdbd" }] : [];
      }

      const segments = [];
      const lots = this.qualityGradingLots || [];
      lots.forEach((lot, i) => {
        const qty = Number(lot.quantity) || 0;
        const pct = Math.round((qty / totalGreenBeans) * 100);
        if (pct > 0) {
          segments.push({
            lotId: lot.lotId || `Batch ${i + 1}`,
            percentage: pct,
            color: PIE_COLORS[i % PIE_COLORS.length],
          });
        }
      });

      const wasteAmount = Number(totalWaste) || 0;
      const wastePct = totalGreenBeans ? Math.round((wasteAmount / totalGreenBeans) * 100) : 0;
      if (wastePct > 0) {
        segments.push({ lotId: "Waste", percentage: wastePct, color: "#bdbdbd" });
      }

      const used = segments.reduce((s, seg) => s + seg.percentage, 0);
      if (used < 100) {
        segments.push({ lotId: "Other", percentage: 100 - used, color: "#e0e0e0" });
      }
      return segments;
    },
    pieSegments() {
      const segments = this.chartSegments.filter((s) => s.percentage > 0);
      if (!segments.length) return [];
      const cx = 110;
      const cy = 110;
      const r = 95;
      const paths = [];
      let currentAngle = -90;
      for (const seg of segments) {
        const sweep = (seg.percentage / 100) * 360;
        paths.push({ ...seg, path: this.describeSlice(cx, cy, r, currentAngle, currentAngle + sweep) });
        currentAngle += sweep;
      }
      return paths;
    },
    outturnChanged() {
      const trimmed = (this.form.outturnNumber || "").trim();
      return trimmed !== (this.originalOutturnNumber || "").trim();
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v && this.itemId) this.load();
    },
    visible(v) {
      this.$emit("input", v);
    },
    itemId(v) {
      if (v && this.visible) this.load();
    },
  },
  methods: {
    close() {
      if (this.submitting || this.batchesLoading || this.outturnChecking) return;
      this.visible = false;
      this.$emit("close");
    },
    toggleSection(key) {
      this.sections[key] = !this.sections[key];
    },
    describeSlice(cx, cy, r, startDeg, endDeg) {
      const sweepDeg = endDeg - startDeg;
      if (sweepDeg >= 360) {
        return `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0`;
      }
      const toRad = (deg) => (deg * Math.PI) / 180;
      const x1 = cx + r * Math.cos(toRad(startDeg));
      const y1 = cy + r * Math.sin(toRad(startDeg));
      const x2 = cx + r * Math.cos(toRad(endDeg));
      const y2 = cy + r * Math.sin(toRad(endDeg));
      const large = endDeg - startDeg > 180 ? 1 : 0;
      return `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
    },
    lotPercentage(lot) {
      const total = Number(this.form.totalGreenBeans) || 0;
      const qty = Number(lot.quantity) || 0;
      if (!total) return "—";
      return Math.round((qty / total) * 100) + "%";
    },
    resolveLabelId(labelTitle) {
      if (!labelTitle || labelTitle === "—") return null;
      const lbl = this.labels.find((l) => l.title === labelTitle);
      return lbl ? lbl.id : null;
    },
    isDbGradingId(id) {
      return id != null && Number.isFinite(Number(id));
    },
    mapGradingsToLots(gradings) {
      return (gradings || []).map((lot) => {
        const unitSize = lot.unitSize != null && lot.unitSize !== "—" ? Number(lot.unitSize) : null;
        const quantity = lot.quantity != null && lot.quantity !== "—" ? Number(lot.quantity) : null;
        const pocketQuantity =
          lot.pocketQuantity != null && lot.pocketQuantity !== "—" ? Number(lot.pocketQuantity) : null;
        const total = Number(quantity) || 0;
        const unit = Number(unitSize) || 1;
        const labelTitle = lot.label;
        const matchedLabel = this.labels.find(
          (l) => l.title === labelTitle || l.title === this.formatStoredLabelDisplay(labelTitle)
        );
        const displayLabel = this.formatStoredLabelDisplay(labelTitle);
        return {
          id: lot.id,
          lotId: lot.lotId && lot.lotId !== "—" ? lot.lotId : "",
          quantity,
          unitSize,
          totalBags: pocketQuantity,
          pocketQuantity,
          leftoverQuantity: unit ? total % unit : total,
          qualityTitle: lot.qualityTitle || lot.qualityScore || "",
          qualityScore: lot.qualityScore && lot.qualityScore !== "—" ? lot.qualityScore : "",
          label:
            matchedLabel
              ? { id: matchedLabel.id, title: matchedLabel.title }
              : displayLabel !== "—"
                ? displayLabel
                : null,
          percentage: lot.percentage != null ? lot.percentage : null,
        };
      });
    },
    formatStoredLabelDisplay(label) {
      if (label == null || label === "" || label === "—") return "—";
      if (typeof label === "object") {
        const title = label.title || label.name;
        if (title) return title;
        return label.id != null ? String(label.id) : "—";
      }
      if (typeof label === "string") {
        const trimmed = label.trim();
        if (!trimmed || trimmed === "{}") return "—";
        if (trimmed.startsWith("{")) {
          try {
            const parsed = JSON.parse(trimmed);
            const title = parsed?.title || parsed?.name;
            if (title) return title;
            if (parsed?.id != null) return String(parsed.id);
            return "—";
          } catch (_) {
            return trimmed === "{}" ? "—" : trimmed;
          }
        }
        return trimmed;
      }
      return "—";
    },
    displayLotLabel(lot) {
      return this.formatStoredLabelDisplay(lot?.label);
    },
    formatLeftover(lot) {
      if (lot.leftoverQuantity != null) return `${lot.leftoverQuantity} kg`;
      const total = Number(lot.quantity) || 0;
      const unit = Number(lot.unitSize) || 1;
      if (!unit) return "—";
      return `${total % unit} kg`;
    },
    openQualityGradingModal() {
      this.qualityGradingModalVisible = true;
    },
    onQualityGradingFinished(lots) {
      this.qualityGradingLots = lots || [];
      this.qualityGradingModalVisible = false;
      if (this.calculatedTotalWaste != null) {
        this.form.totalWaste = this.calculatedTotalWaste;
      }
    },
    dashToEmpty(val) {
      return val == null || val === "—" ? "" : String(val);
    },
    mapDetailBatchToForm(batch) {
      return {
        mapId: batch.mapId || null,
        batchId: this.dashToEmpty(batch.batchId),
        batchCode: batch.batchCode || this.dashToEmpty(batch.batchId),
        processingBatchId: batch.processingBatchId || null,
        quantity: batch.quantity != null && batch.quantity !== "—" ? batch.quantity : "",
        supplier: this.dashToEmpty(batch.supplier),
        species: this.dashToEmpty(batch.species),
        variety: this.dashToEmpty(batch.variety),
        process: this.dashToEmpty(batch.process),
        batchRating: this.dashToEmpty(batch.batchRating),
        humidity: batch.humidity != null && batch.humidity !== "—" ? batch.humidity : "",
        density: batch.density != null && batch.density !== "—" ? batch.density : "",
        totalCoffeeCherryQty:
          batch.totalCoffeeCherryQty != null && batch.totalCoffeeCherryQty !== "—"
            ? batch.totalCoffeeCherryQty
            : "",
      };
    },
    mapProcessingBatchOptionToForm(batch) {
      return {
        mapId: null,
        batchId: batch.batchCode || batch.lotId || "",
        batchCode: batch.batchCode || batch.lotId || "",
        processingBatchId: batch.id || null,
        quantity: batch.quantity != null && batch.quantity !== "" ? batch.quantity : "",
        supplier: batch.supplier && batch.supplier !== "—" ? batch.supplier : "",
        species: batch.typeInfo && batch.typeInfo !== "—" ? batch.typeInfo : "",
        variety: batch.variety && batch.variety !== "—" ? batch.variety : "",
        process: batch.processInfo ? batch.processInfo.replace(/^Process:\s*/i, "") : (batch.process || ""),
        batchRating: batch.batchRating && batch.batchRating !== "—" ? batch.batchRating : "",
        humidity: batch.humidity != null && batch.humidity !== "—" ? batch.humidity : "",
        density: batch.density != null && batch.density !== "—" ? batch.density : "",
        totalCoffeeCherryQty:
          batch.totalCoffeeCherryQty != null && batch.totalCoffeeCherryQty !== "—"
            ? batch.totalCoffeeCherryQty
            : "",
      };
    },
    resolveProcessingBatchId(batch) {
      if (batch?.processingBatchId) return batch.processingBatchId;
      const code = batch?.batchCode || batch?.batchId;
      if (!code || code === "—") return null;
      const found = (this.processingBatches || []).find(
        (b) => b.batchCode === code || b.lotId === code
      );
      return found?.id || null;
    },
    syncBatchesFromSelectedIds(ids) {
      const idSet = new Set((ids || []).map((id) => Number(id)));
      const prevBatches = this.form.batches || [];
      const next = [];
      const usedCodes = new Set();

      idSet.forEach((id) => {
        const existing = prevBatches.find(
          (b) => Number(this.resolveProcessingBatchId(b)) === id
        );
        if (existing) {
          next.push(existing);
          const code = existing.batchCode || existing.batchId;
          if (code) usedCodes.add(code);
          return;
        }
        const opt = (this.processingBatches || []).find((b) => Number(b.id) === id);
        if (opt) {
          const mapped = this.mapProcessingBatchOptionToForm(opt);
          next.push(mapped);
          const code = mapped.batchCode || mapped.batchId;
          if (code) usedCodes.add(code);
        }
      });

      // v-select drops item-value ids that are not in :items; keep still-selected links.
      prevBatches.forEach((batch) => {
        const code = batch.batchCode || batch.batchId;
        if (!code || usedCodes.has(code)) return;

        const resolvedId = this.resolveProcessingBatchId(batch);
        if (resolvedId != null && idSet.has(Number(resolvedId))) {
          next.push(batch);
          usedCodes.add(code);
          return;
        }

        // Batch-code-only links (no dropdown id) — preserve until explicitly cleared.
        if (resolvedId == null) {
          next.push(batch);
          usedCodes.add(code);
        }
      });

      this.form.batches = next;
    },
    applyOutturnBatches(batches) {
      const list = batches || [];
      this.mergeProcessingBatchOptionsFromDetail(list);
      this.form.batches = list.map((batch) => this.mapDetailBatchToForm(batch));
    },
    onOutturnInput(val) {
      this.outturnError = "";
      this.outturnSuccess = "";
      if (this.outturnDebounceTimer) clearTimeout(this.outturnDebounceTimer);
      const trimmed = (val || "").trim();
      if (!trimmed) return;

      if (trimmed === (this.originalOutturnNumber || "").trim()) {
        return;
      }

      this.outturnDebounceTimer = setTimeout(async () => {
        this.outturnChecking = true;
        try {
          const res = await GreenCoffeeService.checkOutturnNumber(trimmed, this.itemId);
          if (res?.data?.available) {
            this.outturnSuccess = this.$t("memberDataSection.greenCoffee.outturnAvailable");
            this.outturnError = "";
            const linkedBatches = res?.data?.batches || [];
            if (trimmed !== this.originalOutturnNumber) {
              this.batchesLoading = true;
              try {
                this.applyOutturnBatches(linkedBatches);
              } finally {
                this.batchesLoading = false;
              }
            }
          } else {
            this.outturnError = this.$t("memberDataSection.greenCoffee.outturnExists");
            this.outturnSuccess = "";
          }
        } catch (_) {
          this.outturnError = this.$t("memberDataSection.greenCoffee.outturnCheckFailed");
          this.outturnSuccess = "";
        } finally {
          this.outturnChecking = false;
        }
      }, 600);
    },
    syncFormFromDetail(d) {
      const setIf = (key, val) => {
        if (val !== null && val !== undefined && val !== "—" && val !== "") this.form[key] = val;
      };

      const outturn = d.outturnNumber && d.outturnNumber !== "—" ? d.outturnNumber : "";
      this.form.outturnNumber = outturn;
      this.originalOutturnNumber = outturn;

      if (d.purchaseDate) this.form.purchaseDate = moment(d.purchaseDate).format("YYYY-MM-DD");
      if (d.totalGreenBeans != null && d.totalGreenBeans !== "—") {
        this.form.totalGreenBeans = Number(d.totalGreenBeans);
      }
      setIf("airHumidity", d.airHumidity);
      setIf("density", d.density);
      setIf("batchProductionDensity", d.batchProductionDensity);
      setIf("weightBeforeProcessing", d.weightBeforeProcessing);
      setIf("primaryDefect", d.primaryDefect);
      setIf("secondaryDefect", d.secondaryDefect);
      if (d.expiryDate) this.form.expiryDate = moment(d.expiryDate).format("YYYY-MM-DD");

      const firstLot = (d.qualityGradings || [])[0];
      if (firstLot?.unitSize) this.form.unitSize = Number(firstLot.unitSize);

      const pt = this.productTypes.find((p) => p.name === d.productType);
      if (pt) this.form.productTypeId = pt.id;

      const qs = this.qualityScores.find((q) => q.name === d.qualityScore);
      if (qs) this.form.greenCoffeeQualityScoreId = qs.id;

      const labelName =
        d.greenBeansLabel && typeof d.greenBeansLabel === "object"
          ? d.greenBeansLabel.title
          : d.greenBeansLabel;
      const lbl = this.labels.find((l) => l.title === labelName);
      if (lbl) this.form.labelId = lbl.id;

      this.form.batches = (d.batches || []).map((batch) => this.mapDetailBatchToForm(batch));
      this.mergeProcessingBatchOptionsFromDetail(d.batches || []);

      this.qualityGradingLots = this.mapGradingsToLots(d.qualityGradings || []);

      if (d.totalWaste != null && d.totalWaste !== "—") {
        this.form.totalWaste = Number(d.totalWaste);
      } else if (this.calculatedTotalWaste != null) {
        // API didn't store a waste value — seed the input from the computed
        // (totalGreenBeans − gradings) so it isn't blank while % shows a value.
        this.form.totalWaste = this.calculatedTotalWaste;
      }
    },
    mergeProcessingBatchOptionsFromDetail(batches) {
      const existingIds = new Set((this.processingBatches || []).map((b) => Number(b.id)));
      const existingCodes = new Set(
        (this.processingBatches || []).flatMap((b) => [b.batchCode, b.lotId].filter(Boolean))
      );
      (batches || []).forEach((batch) => {
        const batchCode = batch.batchCode || batch.batchId;
        if (batch.processingBatchId && existingIds.has(Number(batch.processingBatchId))) return;
        if (!batch.processingBatchId && batchCode && existingCodes.has(batchCode)) return;
        if (!batch.processingBatchId) return;

        this.processingBatches.push({
          id: batch.processingBatchId,
          batchCode: batchCode,
          lotId: batchCode,
          display: batch.batchId || batchCode,
          typeInfo: batch.species || "—",
          processInfo: batch.process ? `Process: ${batch.process}` : "",
          quantity: batch.quantity,
          supplier: batch.supplier || "—",
          variety: batch.variety || "",
          process: batch.process || "",
          batchRating: batch.batchRating || "",
          humidity: batch.humidity,
          density: batch.density,
          totalCoffeeCherryQty: batch.totalCoffeeCherryQty,
        });
        existingIds.add(Number(batch.processingBatchId));
        if (batchCode) existingCodes.add(batchCode);
      });
    },
    async loadOptions() {
      const res = await GreenCoffeeService.getGreenCoffeeOptions(this.itemId);
      const d = res?.data || {};
      this.productTypes = d.productTypes || [];
      this.qualityScores = d.greenCoffeeQuality || [];
      this.labels = d.labels || [];
      this.gradeOptions = d.gradeOptions || [];
      this.processingBatches = d.processingBatches || [];
    },
    async load() {
      this.loading = true;
      this.detail = null;
      try {
        const res = await GreenCoffeeService.getAdminGreenCoffeeById(this.itemId);
        if (res?.data) {
          this.detail = res.data;
          this.resetForm();
          await this.loadOptions();
          this.syncFormFromDetail(res.data);
        }
      } catch (e) {
        console.error("Failed to load green coffee for edit:", e);
        this.$notify?.({
          text: e?.msg || e?.message || "Failed to load record.",
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = {
        outturnNumber: "",
        purchaseDate: moment().format("YYYY-MM-DD"),
        totalGreenBeans: null,
        airHumidity: "",
        density: "",
        batchProductionDensity: "",
        greenCoffeeQualityScoreId: null,
        weightBeforeProcessing: "",
        primaryDefect: "",
        secondaryDefect: "",
        unitSize: isKenyaClient() ? 60 : null,
        expiryDate: moment().format("YYYY-MM-DD"),
        productTypeId: null,
        labelId: null,
        totalWaste: null,
        batches: [],
      };
      this.qualityGradingLots = [];
      this.syncingBatchSelection = false;
      this.originalOutturnNumber = "";
      this.outturnError = "";
      this.outturnSuccess = "";
      this.outturnChecking = false;
      this.batchesLoading = false;
      if (this.outturnDebounceTimer) clearTimeout(this.outturnDebounceTimer);
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    async submit() {
      if (!this.$refs.form?.validate()) return;
      if (this.outturnError || this.outturnChecking || this.batchesLoading) return;
      if (this.outturnChanged && !this.outturnSuccess) {
        this.$notify({
          text: this.$t("memberDataSection.greenCoffee.outturnValidationRequired"),
          type: "error",
        });
        return;
      }

      this.submitting = true;
      try {
        const selectedLabel = this.labels.find((l) => l.id === this.form.labelId);
        const selectedQualityScore = this.qualityScores.find(
          (q) => q.id === this.form.greenCoffeeQualityScoreId
        );
        const totalGreenBeans = Number(this.form.totalGreenBeans) || 0;
        const lotsSum = (this.qualityGradingLots || []).reduce(
          (sum, lot) => sum + (Number(lot.quantity) || 0),
          0
        );

        if (totalGreenBeans > 0 && lotsSum > totalGreenBeans) {
          this.$notify({
            title: this.$t("memberDataSection.greenCoffee.totalGreenBeansExceeded"),
            type: "error",
          });
          return;
        }

        const qualityGradings = (this.qualityGradingLots || []).map((lot) => {
          const labelObj =
            lot.label && typeof lot.label === "object" && lot.label.id != null
              ? { id: lot.label.id }
              : null;
          const payload = {
            lotId: lot.lotId || null,
            quantity: lot.quantity !== "" && lot.quantity != null ? Number(lot.quantity) : null,
            unitSize: lot.unitSize !== "" && lot.unitSize != null ? String(lot.unitSize) : null,
            pocketQuantity:
              lot.totalBags != null && lot.totalBags !== ""
                ? Number(lot.totalBags)
                : lot.pocketQuantity != null && lot.pocketQuantity !== ""
                  ? Number(lot.pocketQuantity)
                  : null,
            qualityScore: lot.qualityScore || null,
            qualityTitle: lot.qualityTitle || lot.qualityScore || "",
            quantityUnit: "kg",
            unitSizeUnit: "kg",
            label: labelObj,
          };
          if (this.isDbGradingId(lot.id)) {
            payload.id = Number(lot.id);
          }
          return payload;
        });

        const totalWaste =
          totalGreenBeans > 0 && lotsSum >= 0
            ? Math.max(totalGreenBeans - lotsSum, 0)
            : null;

        const buyingStationParchments = (this.form.batches || [])
          .filter((b) => b.batchCode || b.batchId)
          .map((b) => ({
            buyingStationParchmentId: b.batchCode || b.batchId,
            quantity: b.quantity !== "" && b.quantity != null ? Number(b.quantity) : null,
          }));

        const payload = {
          productTypeId: this.form.productTypeId || null,
          purchaseDate: this.form.purchaseDate || null,
          outturnNumber: this.form.outturnNumber?.trim() || null,
          qualityControlHumidity: this.form.airHumidity || null,
          qualityControlDensity: this.form.density || null,
          parchmentQualityScore: selectedQualityScore?.name || null,
          parchmentQualityScoreId: this.form.greenCoffeeQualityScoreId || null,
          parchmentChecking: this.form.weightBeforeProcessing || null,
          batchProductionDensity: this.form.batchProductionDensity || null,
          batchProductionPrimaryDefect: this.form.primaryDefect || null,
          batchProductionSecondaryDefect: this.form.secondaryDefect || null,
          greenBeansTotal: totalGreenBeans || null,
          greenBeansBags: this.totalBags || null,
          greenBeansExpiry: this.form.expiryDate || null,
          greenBeansLabel: selectedLabel ? { id: selectedLabel.id } : null,
          qualityGradings,
          buyingStationParchments,
          totalWaste,
        };

        await GreenCoffeeService.updateAdminGreenCoffee(this.itemId, payload);
        this.$emit("submitted");
        // Clear the in-flight flag BEFORE close() — close() bails while
        // `submitting` is true, so leaving it set here blocks the dialog
        // from closing on a successful save.
        this.submitting = false;
        this.close();
      } catch (err) {
        console.error("Failed to update green coffee:", err);
        this.$notify?.({
          text: err?.msg || err?.message || "Failed to update record.",
          type: "error",
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.section-header {
  cursor: pointer;
  user-select: none;
  background-color: #fafafa;
}
.section-header:hover {
  background-color: #f5f5f5;
}
.section-title {
  color: #6d4c41;
  font-weight: 700;
  font-size: 15px;
}
.lot-id {
  color: #6d4c41;
  font-weight: 700;
  font-size: 15px;
}
.field-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  line-height: 1.4;
}
.field-value {
  font-weight: 700;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  margin-top: 2px;
}
.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
</style>
